import {
  GRAVITY,
  JUMP_VELOCITY,
  PLAYER_SPEED,
  PLAYER_SPRINT_SPEED,
  PLAYER_WIDTH,
  PLAYER_HEIGHT,
  BlockType,
  computeFallDamage,
} from "@craftgaming/shared";
import { clamp } from "@craftgaming/shared";
import { resolveCollision, type WorldBlockAccess } from "@craftgaming/shared";
import type { InputState } from "../core/input";

const MOUSE_SENSITIVITY = 0.002;
const MAX_FALL_SPEED = 78.4;
// Knockback is a decaying impulse ADDED on top of movement — the old
// code set state.vx/vz directly, which update() overwrote the next frame
// with movement velocity, so the hit never actually pushed the player.
const KNOCKBACK_SPEED = 8; // horizontal tranco (blocks/s at impact)
const KNOCKBACK_VERTICAL = 4.5; // upward pop, like Minecraft's hit bounce
const KNOCKBACK_DECAY = 7; // higher = shorter shove (~1 block of travel)

// Water physics
const WATER_GRAVITY = 10; // entering water pulls you down noticeably
const WATER_MAX_SINK_SPEED = 4;
const WATER_SWIM_UP_SPEED = 4.5; // hold jump to swim up
const WATER_MOVE_FACTOR = 0.55; // horizontal drag
const WATER_JUMP_VELOCITY = 6; // shallow-water hop — NOT a full dry-land jump

// Creative flight
const FLY_SPEED_FACTOR = 1.7;
const FLY_VERTICAL_SPEED = 8;
const DOUBLE_JUMP_WINDOW_MS = 300;

export type PlayerState = {
  x: number;
  y: number; // feet position
  z: number;
  vx: number;
  vy: number;
  vz: number;
  onGround: boolean;
  yaw: number;
  pitch: number;
  fallStartY: number | null;
  inWater: boolean;
  flying: boolean; // creative mode only
  sprinting: boolean; // actually running this frame (for FOV/hunger)
};

export type PlayerController = {
  state: PlayerState;
  update: (dt: number, input: InputState, isPointerLocked: boolean) => void;
  applyKnockback: (dirX: number, dirZ: number) => void;
  consumeFallDamage: () => number;
};

export function createPlayerController(
  spawnX: number,
  spawnY: number,
  spawnZ: number,
  worldAccess: WorldBlockAccess,
  options: { creative?: boolean; canSprint?: () => boolean } = {}
): PlayerController {
  const state: PlayerState = {
    x: spawnX,
    y: spawnY,
    z: spawnZ,
    vx: 0,
    vy: 0,
    vz: 0,
    onGround: false,
    yaw: 0,
    pitch: 0,
    fallStartY: null,
    inWater: false,
    flying: false,
    sprinting: false,
  };

  let pendingFallDamage = 0;
  let lastJumpPress = 0;
  let prevJump = false;
  // Decaying knockback impulse, added to movement velocity each frame.
  let knockbackX = 0;
  let knockbackZ = 0;

  function blockAt(offsetY: number): BlockType {
    return worldAccess.getBlockAtWorld(
      Math.floor(state.x),
      Math.floor(state.y + offsetY),
      Math.floor(state.z)
    );
  }

  function isInWater(): boolean {
    // Body counts as in water when feet or torso are submerged
    return blockAt(0.2) === BlockType.WATER || blockAt(1.0) === BlockType.WATER;
  }

  /** Solid block within a step in the facing direction, at feet or eye level. */
  function hasSolidBlockAhead(): boolean {
    const aheadX = state.x - Math.sin(state.yaw) * 0.8;
    const aheadZ = state.z - Math.cos(state.yaw) * 0.8;
    for (const dy of [0.2, 1.0]) {
      const block = worldAccess.getBlockAtWorld(
        Math.floor(aheadX),
        Math.floor(state.y + dy),
        Math.floor(aheadZ)
      );
      if (block !== BlockType.AIR && block !== BlockType.WATER) return true;
    }
    return false;
  }

  function applyKnockback(dirX: number, dirZ: number) {
    // Store as a separate impulse so update() adds it on top of movement
    // instead of clobbering it. Normalize the direction defensively.
    const len = Math.hypot(dirX, dirZ) || 1;
    knockbackX = (dirX / len) * KNOCKBACK_SPEED;
    knockbackZ = (dirZ / len) * KNOCKBACK_SPEED;
    // Always pop upward a little (even on the ground) so the hit reads as
    // a real Minecraft-style bounce, not just a horizontal slide.
    state.vy = KNOCKBACK_VERTICAL;
    state.onGround = false;
  }

  function consumeFallDamage(): number {
    const dmg = pendingFallDamage;
    pendingFallDamage = 0;
    return dmg;
  }

  function update(dt: number, input: InputState, isPointerLocked: boolean) {
    // Camera rotation from mouse
    if (isPointerLocked) {
      state.yaw -= input.mouseDeltaX * MOUSE_SENSITIVITY;
      state.pitch -= input.mouseDeltaY * MOUSE_SENSITIVITY;
      state.pitch = clamp(state.pitch, -Math.PI / 2 + 0.01, Math.PI / 2 - 0.01);
    }

    // Movement input → direction
    let moveX = 0;
    let moveZ = 0;
    if (input.forward) moveZ -= 1;
    if (input.backward) moveZ += 1;
    if (input.left) moveX -= 1;
    if (input.right) moveX += 1;

    // Normalize diagonal
    const len = Math.sqrt(moveX * moveX + moveZ * moveZ);
    if (len > 0) {
      moveX /= len;
      moveZ /= len;
    }

    // Rotate by yaw (camera forward = -sin(yaw), -cos(yaw))
    const sinYaw = Math.sin(state.yaw);
    const cosYaw = Math.cos(state.yaw);
    const worldMoveX = moveX * cosYaw + moveZ * sinYaw;
    const worldMoveZ = -moveX * sinYaw + moveZ * cosYaw;

    const inWater = isInWater();
    state.inWater = inWater;

    // Creative: double-tap jump toggles flight
    const jumpJustPressed = input.jump && !prevJump;
    prevJump = input.jump;
    if (options.creative && jumpJustPressed) {
      const now = performance.now();
      if (now - lastJumpPress < DOUBLE_JUMP_WINDOW_MS) {
        state.flying = !state.flying;
        state.vy = 0;
        state.fallStartY = null;
      }
      lastJumpPress = now;
    }
    // Touching the ground cancels flight
    if (state.flying && state.onGround) {
      state.flying = false;
    }

    // Speed — sprinting requires stamina (low hunger = too tired to run)
    const sprintAllowed = options.canSprint?.() ?? true;
    const wantsSprint = input.sprint && sprintAllowed && !state.flying;
    let speed = wantsSprint ? PLAYER_SPRINT_SPEED : PLAYER_SPEED;
    if (state.flying) speed = PLAYER_SPRINT_SPEED * FLY_SPEED_FACTOR;
    else if (inWater) speed *= WATER_MOVE_FACTOR;

    state.sprinting = wantsSprint && !inWater && len > 0;

    // Horizontal velocity (instant, no acceleration for snappy feel) plus
    // any active knockback impulse, which decays exponentially so a hit
    // shoves you back and then hands control cleanly back to movement.
    state.vx = worldMoveX * speed + knockbackX;
    state.vz = worldMoveZ * speed + knockbackZ;
    const knockDecay = Math.exp(-KNOCKBACK_DECAY * dt);
    knockbackX *= knockDecay;
    knockbackZ *= knockDecay;
    if (Math.abs(knockbackX) < 0.05) knockbackX = 0;
    if (Math.abs(knockbackZ) < 0.05) knockbackZ = 0;

    if (state.flying) {
      // Free flight: jump rises, sprint (shift) descends, no gravity
      state.vy = input.jump ? FLY_VERTICAL_SPEED : input.sprint ? -FLY_VERTICAL_SPEED : 0;
      state.fallStartY = null;
    } else if (inWater) {
      // Water: sink, hold jump to swim up; falls never hurt.
      // Water is NEVER solid ground — jumps here are weak water-hops,
      // strong enough to climb a 1-block bank, never a dry-land jump.
      // Water is never solid ground: no ground-jump. Holding jump only
      // climbs when a real bank is ahead (hasSolidBlockAhead excludes
      // water), otherwise it's a gentle swim-up — never a surface hop.
      state.fallStartY = null;
      if (input.jump) {
        if (hasSolidBlockAhead()) {
          // Only a real bank ahead gives a climb boost — never a bounce
          // on the open water surface (that read as "jumping on water")
          state.vy = WATER_JUMP_VELOCITY + 1.5;
        } else {
          state.vy = WATER_SWIM_UP_SPEED; // swim up gently, no hop
        }
      } else {
        state.vy -= WATER_GRAVITY * dt;
        state.vy = Math.max(state.vy, -WATER_MAX_SINK_SPEED);
      }
    } else {
      // Gravity
      if (!state.onGround) {
        state.vy -= GRAVITY * dt;
        state.vy = Math.max(state.vy, -MAX_FALL_SPEED);
      }

      // Jump
      if (input.jump && state.onGround) {
        state.vy = JUMP_VELOCITY;
        state.onGround = false;
      }
    }

    // Resolve collision
    const result = resolveCollision(
      state.x, state.y, state.z,
      state.vx, state.vy, state.vz,
      PLAYER_WIDTH, PLAYER_HEIGHT,
      dt,
      worldAccess
    );

    // Fall damage tracking
    const wasOnGround = state.onGround;

    state.x = result.x;
    state.y = result.y;
    state.z = result.z;
    state.onGround = result.onGround;

    // Start tracking the fall at its apex — covers both walking off an
    // edge AND jumping (a jump starts with vy > 0, so the old
    // wasOnGround-only check never armed fall damage for jumps).
    if (!result.onGround && !state.flying && !inWater && state.vy <= 0 && state.fallStartY === null) {
      state.fallStartY = state.y;
    }

    // Landed → compute fall damage
    if (!wasOnGround && result.onGround && state.fallStartY !== null) {
      const fallDistance = state.fallStartY - state.y;
      if (fallDistance > 0 && !options.creative) {
        pendingFallDamage += computeFallDamage(fallDistance, isInWater());
      }
      state.fallStartY = null;
    }

    // Stop vertical velocity on ground/ceiling
    if (result.onGround && state.vy < 0) state.vy = 0;
    if (result.hitCeiling && state.vy > 0) state.vy = 0;
  }

  return { state, update, applyKnockback, consumeFallDamage };
}
